import React from 'react';

let notificationInstance = null;

const NotificationItem = ({ notification, onClose }) => {
  const colors = {
    success: { bg: '#10b981', border: '#059669' },
    error: { bg: '#ef4444', border: '#dc2626' },
    warning: { bg: '#f59e0b', border: '#d97706' },
    info: { bg: '#3b82f6', border: '#2563eb' }
  };

  const color = colors[notification.type] || colors.info;

  return (
    <div
      style={{
        backgroundColor: color.bg,
        borderLeft: `4px solid ${color.border}`,
        color: 'white',
        padding: '0.75rem 1rem',
        borderRadius: '0.5rem',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        minWidth: '280px',
        maxWidth: '380px',
        animation: notification.fadingOut
          ? 'fadeOut 0.4s ease-out forwards'
          : 'slideIn 0.3s ease-out'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
            {notification.message.title}
          </div>
          {notification.message.description && (
            <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>
              {notification.message.description}
            </div>
          )}
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            color: 'white',
            cursor: 'pointer',
            fontSize: '1.25rem',
            padding: '0 0.5rem',
            marginLeft: '0.5rem',
            opacity: 0.8
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
};

export const Notification = () => {
  const [notifications, setNotifications] = React.useState([]);

  React.useEffect(() => {
    notificationInstance = {
      show: (message, type = 'info', duration = 3500) => {
        const id = Date.now();
        setNotifications(prev => [...prev, { id, message, type, fadingOut: false }]);

        if (duration > 0) {
          setTimeout(() => {
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, fadingOut: true } : n));
            setTimeout(() => {
              setNotifications(prev => prev.filter(n => n.id !== id));
            }, 400);
          }, duration);
        }
      },
      clear: () => setNotifications([])
    };

    // Add keyframes for animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideIn {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
      @keyframes fadeOut {
        from { opacity: 1; }
        to { opacity: 0; transform: translateX(20px); }
      }
    `;
    document.head.appendChild(style);

    return () => {
      notificationInstance = null;
      document.head.removeChild(style);
    };
  }, []);

  if (notifications.length === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '1.5rem',
        right: '1.5rem',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem'
      }}
    >
      {notifications.map(notification => (
        <NotificationItem
          key={notification.id}
          notification={notification}
          onClose={() => {
            setNotifications(prev => prev.filter(n => n.id !== notification.id));
          }}
        />
      ))}
    </div>
  );
};

export const showNotification = (title, description, type = 'info') => {
  if (notificationInstance) {
    notificationInstance.show({ title, description }, type);
  } else {
    console.warn('Notification system not initialized');
  }
};

export const showSuccessNotification = (title, description) => {
  showNotification(title, description, 'success');
};

export const showErrorNotification = (title, description) => {
  showNotification(title, description, 'error');
};

export const showWarningNotification = (title, description) => {
  showNotification(title, description, 'warning');
};

export const showInfoNotification = (title, description) => {
  showNotification(title, description, 'info');
};
